import { useState, useEffect, useCallback, useRef } from "react";
import { createFileRoute } from "@tanstack/react-router";
import {
  Layers,
  Plus,
  Settings2,
  Trash2,
  RefreshCw,
  Loader2,
  DollarSign,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { SearchInput } from "@/components/ui/search-input";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import {
  Table,
  TableBody,
  TableCell,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { SortableTableHead } from "@/components/ui/sortable-table-head";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Pagination } from "@/components/ui/pagination";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { Checkbox } from "@/components/ui/checkbox";
import {
  platformApi,
  ServiceTier,
  ServiceTierCreateRequest,
  ServiceTierUpdateRequest,
  ServiceFeature,
  ServiceTierFeature,
  PlatformService,
} from "@/services/api/platform";
import { usePaginatedData, PaginatedResponse } from "@/hooks/usePaginatedData";

export const Route = createFileRoute("/portal/platform/tiers")({
  component: TiersPage,
});

// ---------------------------------------------------------------------------
// Inline editable cell — click to edit, blur/Enter to save
// ---------------------------------------------------------------------------
function EditableCell({
  value,
  onSave,
  type = "text",
  prefix = "",
  suffix = "",
  className = "",
}: {
  value: string | number;
  onSave: (value: string | number) => void;
  type?: "text" | "number";
  prefix?: string;
  suffix?: string;
  className?: string;
}) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(String(value));
  const inputRef = useRef<HTMLInputElement>(null);

  function startEdit() {
    setDraft(String(value));
    setEditing(true);
    // Focus after render
    setTimeout(() => inputRef.current?.focus(), 0);
  }

  function commit() {
    setEditing(false);
    const parsed = type === "number" ? parseInt(draft) || 0 : draft;
    if (parsed !== value) {
      onSave(parsed);
    }
  }

  function handleKeyDown(e: React.KeyboardEvent) {
    if (e.key === "Enter") {
      commit();
    } else if (e.key === "Escape") {
      setEditing(false);
    }
  }

  if (editing) {
    return (
      <Input
        ref={inputRef}
        type={type}
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onBlur={commit}
        onKeyDown={handleKeyDown}
        className={`h-7 w-full text-sm ${className}`}
      />
    );
  }

  return (
    <span
      onClick={startEdit}
      className={`cursor-pointer rounded px-1.5 py-0.5 hover:bg-muted transition-colors ${className}`}
      title="Click to edit"
    >
      {prefix}{type === "number" ? value.toLocaleString() : value}{suffix}
    </span>
  );
}


function TiersPage() {
  const { token } = useAuth();
  const [services, setServices] = useState<PlatformService[]>([]);
  const [showInactive, setShowInactive] = useState(false);
  const [selectedServiceFilter, setSelectedServiceFilter] = useState<string>("all");
  const [searchTerm, setSearchTerm] = useState("");
  const [sortBy, setSortBy] = useState("sort_order");
  const [sortOrder, setSortOrder] = useState<"asc" | "desc">("asc");
  const [deleteTier, setDeleteTier] = useState<ServiceTier | null>(null);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  // Limits & Features dialog (replaces old full Edit dialog)
  const [limitsDialogTier, setLimitsDialogTier] = useState<ServiceTier | null>(null);
  const [allowancesJson, setAllowancesJson] = useState("");
  const [overagePricingJson, setOveragePricingJson] = useState("");

  // Tier feature checklist (replaces Features JSON editor)
  const [serviceFeatures, setServiceFeatures] = useState<ServiceFeature[]>([]);
  const [tierFeatureIds, setTierFeatureIds] = useState<Set<string>>(new Set());
  const [loadingFeatures, setLoadingFeatures] = useState(false);

  // Create form state
  const [createForm, setCreateForm] = useState<ServiceTierCreateRequest>({
    service_id: "",
    name: "",
    display_name: "",
    sort_order: 0,
    price_monthly_usd: 0,
    price_monthly_zar: 0,
    price_annual_usd: 0,
    price_annual_zar: 0,
    allowances: {},
    features: {},
    overage_pricing: {},
    contact_sales: false,
    is_active: true,
  });
  const [createAllowancesJson, setCreateAllowancesJson] = useState("");
  const [createOveragePricingJson, setCreateOveragePricingJson] = useState("");

  // Server-side pagination with search
  const fetchTiers = useCallback(
    async (page: number, pageSize: number): Promise<PaginatedResponse<ServiceTier>> => {
      if (!token) {
        return { data: [], totalCount: 0, page, pageSize };
      }
      try {
        const response = await platformApi.listAllTiers(token, {
          include_inactive: showInactive,
          service_id: selectedServiceFilter !== "all" ? selectedServiceFilter : undefined,
          search: searchTerm || undefined,
          offset: (page - 1) * pageSize,
          limit: pageSize,
          sort_by: sortBy,
          sort_order: sortOrder,
        });
        return {
          data: response.items || [],
          totalCount: response.total,
          page,
          pageSize,
        };
      } catch (error) {
        console.error("Failed to load tiers:", error);
        toast.error("Failed to load tiers");
        return { data: [], totalCount: 0, page, pageSize };
      }
    },
    // `services` was listed but never read in here; it changes on every
    // services refetch, so keeping it re-created the callback and re-ran the
    // tier query for nothing.
    [token, showInactive, selectedServiceFilter, searchTerm, sortBy, sortOrder],
  );

  const {
    data: tiers,
    isLoading,
    pagination,
    goToPage,
    nextPage,
    prevPage,
    firstPage,
    lastPage,
    setPageSize,
    refresh,
    canGoNext,
    canGoPrev,
    startIndex,
    endIndex,
  } = usePaginatedData<ServiceTier>({
    fetchFn: fetchTiers,
    initialPageSize: 25,
    dependencies: [token, showInactive, selectedServiceFilter, searchTerm, sortBy, sortOrder, services],
  });

  function handleSort(column: string) {
    if (sortBy === column) {
      setSortOrder(sortOrder === "asc" ? "desc" : "asc");
    } else {
      setSortBy(column);
      setSortOrder("asc");
    }
  }
  function getSortDirection(column: string): "asc" | "desc" | null {
    return sortBy === column ? sortOrder : null;
  }

  // Load services for filter dropdown
  useEffect(() => {
    if (!token) return;
    platformApi
      .listPlatformServices(token, true)
      .then(setServices)
      .catch((err) => {
        console.error("Failed to load services:", err);
      });
  }, [token]);

  // -----------------------------------------------------------------------
  // Inline save — update a single field (or multiple) on a tier
  // -----------------------------------------------------------------------
  async function inlineSave(tier: ServiceTier, updates: Partial<ServiceTierUpdateRequest>) {
    if (!token) return;
    try {
      await platformApi.updateTier(token, tier.id, updates);
      refresh();
    } catch (error) {
      console.error("Failed to update tier:", error);
      toast.error("Failed to save change");
    }
  }

  async function handleCreateTier() {
    if (!token) return;
    setIsSaving(true);
    try {
      let allowances = {};
      let overagePricing = {};

      if (createAllowancesJson.trim()) {
        try {
          allowances = JSON.parse(createAllowancesJson);
        } catch {
          toast.error("Invalid JSON in allowances");
          setIsSaving(false);
          return;
        }
      }
      if (createOveragePricingJson.trim()) {
        try {
          overagePricing = JSON.parse(createOveragePricingJson);
        } catch {
          toast.error("Invalid JSON in overage pricing");
          setIsSaving(false);
          return;
        }
      }

      await platformApi.createTier(token, {
        ...createForm,
        allowances,
        overage_pricing: overagePricing,
      });
      toast.success(`Created tier: ${createForm.display_name}`);
      setShowCreateDialog(false);
      resetCreateForm();
      refresh();
    } catch (error) {
      console.error("Failed to create tier:", error);
      toast.error("Failed to create tier");
    } finally {
      setIsSaving(false);
    }
  }

  // Save Limits & Features dialog
  async function handleSaveLimits() {
    if (!token || !limitsDialogTier) return;
    setIsSaving(true);
    try {
      let allowances = limitsDialogTier.allowances || {};
      let overagePricing = limitsDialogTier.overage_pricing || {};

      if (allowancesJson.trim()) {
        try {
          allowances = JSON.parse(allowancesJson);
        } catch {
          toast.error("Invalid JSON in allowances");
          setIsSaving(false);
          return;
        }
      }
      if (overagePricingJson.trim()) {
        try {
          overagePricing = JSON.parse(overagePricingJson);
        } catch {
          toast.error("Invalid JSON in overage pricing");
          setIsSaving(false);
          return;
        }
      }

      await platformApi.updateTier(token, limitsDialogTier.id, {
        allowances,
        overage_pricing: overagePricing,
      });
      toast.success(`Updated limits for ${limitsDialogTier.display_name}`);
      setLimitsDialogTier(null);
      refresh();
    } catch (error) {
      console.error("Failed to update limits:", error);
      toast.error("Failed to update limits");
    } finally {
      setIsSaving(false);
    }
  }

  async function handleDeleteTier(tier: ServiceTier) {
    if (!token) return;
    try {
      await platformApi.deleteTier(token, tier.id);
      toast.success(`Deactivated ${tier.display_name}`);
      setDeleteTier(null);
      refresh();
    } catch (error) {
      console.error("Failed to delete tier:", error);
      toast.error("Failed to deactivate tier");
    }
  }

  function resetCreateForm() {
    setCreateForm({
      service_id: "",
      name: "",
      display_name: "",
      sort_order: 0,
      price_monthly_usd: 0,
      price_monthly_zar: 0,
      price_annual_usd: 0,
      price_annual_zar: 0,
      allowances: {},
      features: {},
      overage_pricing: {},
      contact_sales: false,
      is_active: true,
    });
    setCreateAllowancesJson("");
    setCreateOveragePricingJson("");
  }

  async function openLimitsDialog(tier: ServiceTier) {
    setLimitsDialogTier({ ...tier });
    setAllowancesJson(tier.allowances ? JSON.stringify(tier.allowances, null, 2) : "");
    setOveragePricingJson(tier.overage_pricing ? JSON.stringify(tier.overage_pricing, null, 2) : "");

    // Load features for this tier's service + current tier linkages
    if (token && tier.service_id) {
      setLoadingFeatures(true);
      try {
        const [featResp, tierFeats] = await Promise.all([
          platformApi.listServiceFeatures(token, tier.service_id),
          platformApi.listTierFeatures(token, tier.id),
        ]);
        setServiceFeatures(featResp.features || []);
        setTierFeatureIds(new Set(tierFeats.map((tf: ServiceTierFeature) => tf.service_feature_id)));
      } catch {
        setServiceFeatures([]);
        setTierFeatureIds(new Set());
      } finally {
        setLoadingFeatures(false);
      }
    }
  }

  async function toggleTierFeature(featureId: string, checked: boolean) {
    if (!token || !limitsDialogTier) return;
    try {
      if (checked) {
        await platformApi.addTierFeature(token, limitsDialogTier.id, featureId);
        setTierFeatureIds((prev) => new Set([...prev, featureId]));
      } else {
        await platformApi.removeTierFeature(token, limitsDialogTier.id, featureId);
        setTierFeatureIds((prev) => {
          const next = new Set(prev);
          next.delete(featureId);
          return next;
        });
      }
    } catch {
      toast.error("Failed to update tier feature");
    }
  }

  function formatPrice(cents: number, currency: "usd" | "zar"): string {
    const amount = cents / 100;
    return currency === "usd"
      ? `$${amount.toLocaleString()}`
      : `R${amount.toLocaleString()}`;
  }

  function getServiceName(serviceId: string): string {
    const service = services.find((s) => s.id === serviceId);
    return service?.display_name || service?.name || "Unknown";
  }


  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight flex items-center gap-2">
            <DollarSign className="h-6 w-6" />
            Service Tiers (Admin)
          </h1>
          <p className="text-muted-foreground">
            Manage pricing tiers, allowances, and features for each service.
            Click any value to edit inline.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <div className="flex items-center space-x-2">
            <Switch
              id="show-inactive"
              checked={showInactive}
              onCheckedChange={setShowInactive}
            />
            <Label htmlFor="show-inactive">Show inactive</Label>
          </div>
          <Button variant="outline" onClick={() => refresh()} disabled={isLoading}>
            <RefreshCw className={`h-4 w-4 mr-2 ${isLoading ? "animate-spin" : ""}`} />
            Refresh
          </Button>
          <Button onClick={() => setShowCreateDialog(true)}>
            <Plus className="h-4 w-4 mr-2" />
            Add Tier
          </Button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">Total Tiers</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{pagination.totalCount}</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">On This Page</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{tiers.length}</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">Active</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-green-600">
              {tiers.filter((t) => t.is_active).length}
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">Contact Sales</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {tiers.filter((t) => t.contact_sales).length}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Tiers Table — inline editable */}
      <Card>
        <CardHeader className="py-4">
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="text-base">Service Tiers</CardTitle>
              <CardDescription className="text-xs">
                Pricing and allowances by service — click values to edit
              </CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent className="pt-0">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
            <SearchInput
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onClear={() => setSearchTerm("")}
              placeholder="Search by service or tier name..."
              containerClassName="md:col-span-2"
            />
            <Select value={selectedServiceFilter} onValueChange={setSelectedServiceFilter}>
              <SelectTrigger className="h-9">
                <SelectValue placeholder="Filter by service" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Services</SelectItem>
                {services.map((service) => (
                    <SelectItem key={service.id} value={service.id}>
                      {service.display_name}
                    </SelectItem>
                  ))}
              </SelectContent>
            </Select>
          </div>
          {isLoading && tiers.length === 0 ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
            </div>
          ) : tiers.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              <Layers className="h-12 w-12 mx-auto mb-4 opacity-50" />
              <p>No tiers found. Click "Add Tier" to create one.</p>
            </div>
          ) : (
            <>
              <Table>
                <TableHeader>
                  <TableRow>
                    <SortableTableHead
                      direction={getSortDirection("service")}
                      onSort={() => handleSort("service")}
                    >
                      Service
                    </SortableTableHead>
                    <SortableTableHead
                      direction={getSortDirection("display_name")}
                      onSort={() => handleSort("display_name")}
                    >
                      Tier
                    </SortableTableHead>
                    <SortableTableHead
                      direction={getSortDirection("is_active")}
                      onSort={() => handleSort("is_active")}
                    >
                      Active
                    </SortableTableHead>
                    <SortableTableHead
                      direction={getSortDirection("price_monthly_usd")}
                      onSort={() => handleSort("price_monthly_usd")}
                    >
                      USD/mo
                    </SortableTableHead>
                    <SortableTableHead
                      direction={getSortDirection("price_monthly_zar")}
                      onSort={() => handleSort("price_monthly_zar")}
                    >
                      ZAR/mo
                    </SortableTableHead>
                    <SortableTableHead
                      direction={getSortDirection("price_annual_usd")}
                      onSort={() => handleSort("price_annual_usd")}
                    >
                      USD/yr
                    </SortableTableHead>
                    <SortableTableHead
                      direction={getSortDirection("price_annual_zar")}
                      onSort={() => handleSort("price_annual_zar")}
                    >
                      ZAR/yr
                    </SortableTableHead>
                    <SortableTableHead
                      direction={getSortDirection("contact_sales")}
                      onSort={() => handleSort("contact_sales")}
                    >
                      Sales
                    </SortableTableHead>
                    <SortableTableHead direction={null} onSort={() => {}} disabled>
                      Actions
                    </SortableTableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {tiers.map((tier) => (
                    <TableRow key={tier.id}>
                      {/* Service — read-only */}
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <Layers className="h-4 w-4 text-muted-foreground shrink-0" />
                          <span className="text-sm">{getServiceName(tier.service_id)}</span>
                        </div>
                      </TableCell>

                      {/* Tier name — display_name editable, slug readonly */}
                      <TableCell>
                        <div>
                          <EditableCell
                            value={tier.display_name}
                            onSave={(v) => inlineSave(tier, { display_name: String(v) })}
                            className="font-medium"
                          />
                          <div className="text-xs text-muted-foreground mt-0.5">
                            {tier.name}
                            {" "}
                            <EditableCell
                              value={tier.sort_order}
                              type="number"
                              prefix="(#"
                              suffix=")"
                              onSave={(v) => inlineSave(tier, { sort_order: Number(v) })}
                              className="text-xs"
                            />
                          </div>
                        </div>
                      </TableCell>

                      {/* Active toggle */}
                      <TableCell>
                        <Switch
                          checked={tier.is_active}
                          onCheckedChange={(checked) =>
                            inlineSave(tier, { is_active: checked })
                          }
                        />
                      </TableCell>

                      {/* Monthly USD (cents) */}
                      <TableCell>
                        <EditableCell
                          value={tier.price_monthly_usd}
                          type="number"
                          onSave={(v) => inlineSave(tier, { price_monthly_usd: Number(v) })}
                          className="text-sm"
                        />
                        <div className="text-xs text-muted-foreground">
                          {formatPrice(tier.price_monthly_usd, "usd")}
                        </div>
                      </TableCell>

                      {/* Monthly ZAR (cents) */}
                      <TableCell>
                        <EditableCell
                          value={tier.price_monthly_zar}
                          type="number"
                          onSave={(v) => inlineSave(tier, { price_monthly_zar: Number(v) })}
                          className="text-sm"
                        />
                        <div className="text-xs text-muted-foreground">
                          {formatPrice(tier.price_monthly_zar, "zar")}
                        </div>
                      </TableCell>

                      {/* Annual USD (cents) */}
                      <TableCell>
                        <EditableCell
                          value={tier.price_annual_usd}
                          type="number"
                          onSave={(v) => inlineSave(tier, { price_annual_usd: Number(v) })}
                          className="text-sm"
                        />
                        <div className="text-xs text-muted-foreground">
                          {formatPrice(tier.price_annual_usd, "usd")}
                        </div>
                      </TableCell>

                      {/* Annual ZAR (cents) */}
                      <TableCell>
                        <EditableCell
                          value={tier.price_annual_zar}
                          type="number"
                          onSave={(v) => inlineSave(tier, { price_annual_zar: Number(v) })}
                          className="text-sm"
                        />
                        <div className="text-xs text-muted-foreground">
                          {formatPrice(tier.price_annual_zar, "zar")}
                        </div>
                      </TableCell>

                      {/* Contact Sales toggle */}
                      <TableCell>
                        <Switch
                          checked={tier.contact_sales}
                          onCheckedChange={(checked) =>
                            inlineSave(tier, { contact_sales: checked })
                          }
                        />
                      </TableCell>

                      {/* Actions */}
                      <TableCell className="text-right">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => openLimitsDialog(tier)}
                          title="Edit limits & features"
                        >
                          <Settings2 className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => setDeleteTier(tier)}
                          disabled={!tier.is_active}
                        >
                          <Trash2 className="h-4 w-4 text-destructive" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>

              {/* Pagination */}
              <div className="mt-4">
                <Pagination
                  page={pagination.page}
                  totalPages={pagination.totalPages}
                  totalCount={pagination.totalCount}
                  pageSize={pagination.pageSize}
                  pageSizeOptions={[10, 25, 50, 100]}
                  startIndex={startIndex}
                  endIndex={endIndex}
                  canGoPrev={canGoPrev}
                  canGoNext={canGoNext}
                  onPageChange={goToPage}
                  onPageSizeChange={setPageSize}
                  onFirstPage={firstPage}
                  onLastPage={lastPage}
                  onPrevPage={prevPage}
                  onNextPage={nextPage}
                />
              </div>
            </>
          )}
        </CardContent>
      </Card>

      {/* Create Dialog — kept as-is, still needs full form */}
      <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Create Service Tier</DialogTitle>
            <DialogDescription>
              Add a new pricing tier for a service
            </DialogDescription>
          </DialogHeader>
          <Tabs defaultValue="basic" className="w-full">
            <TabsList className="grid w-full grid-cols-3">
              <TabsTrigger value="basic">Basic</TabsTrigger>
              <TabsTrigger value="pricing">Pricing</TabsTrigger>
              <TabsTrigger value="limits">Limits & Features</TabsTrigger>
            </TabsList>
            <TabsContent value="basic" className="space-y-4 py-4">
              <div className="space-y-2">
                <Label>Service</Label>
                <Select
                  value={createForm.service_id}
                  onValueChange={(value) =>
                    setCreateForm({ ...createForm, service_id: value })
                  }
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select a service" />
                  </SelectTrigger>
                  <SelectContent>
                    {services
                      .filter((s) => s.is_active)
                      .map((service) => (
                        <SelectItem key={service.id} value={service.id}>
                          {service.display_name}
                        </SelectItem>
                      ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Name (identifier)</Label>
                  <Input
                    value={createForm.name}
                    onChange={(e) =>
                      setCreateForm({ ...createForm, name: e.target.value })
                    }
                    placeholder="e.g., starter, professional"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Display Name</Label>
                  <Input
                    value={createForm.display_name}
                    onChange={(e) =>
                      setCreateForm({ ...createForm, display_name: e.target.value })
                    }
                    placeholder="e.g., Starter, Professional"
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label>Sort Order</Label>
                <Input
                  type="number"
                  value={createForm.sort_order}
                  onChange={(e) =>
                    setCreateForm({ ...createForm, sort_order: parseInt(e.target.value) || 0 })
                  }
                />
              </div>
              <div className="flex items-center justify-between">
                <div className="space-y-0.5">
                  <Label>Active</Label>
                  <p className="text-sm text-muted-foreground">
                    Tier is available for subscription
                  </p>
                </div>
                <Switch
                  checked={createForm.is_active}
                  onCheckedChange={(checked) =>
                    setCreateForm({ ...createForm, is_active: checked })
                  }
                />
              </div>
              <div className="flex items-center justify-between">
                <div className="space-y-0.5">
                  <Label>Contact Sales</Label>
                  <p className="text-sm text-muted-foreground">
                    Requires sales contact for pricing
                  </p>
                </div>
                <Switch
                  checked={createForm.contact_sales}
                  onCheckedChange={(checked) =>
                    setCreateForm({ ...createForm, contact_sales: checked })
                  }
                />
              </div>
            </TabsContent>
            <TabsContent value="pricing" className="space-y-4 py-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Monthly Price (USD cents)</Label>
                  <Input
                    type="number"
                    value={createForm.price_monthly_usd}
                    onChange={(e) =>
                      setCreateForm({ ...createForm, price_monthly_usd: parseInt(e.target.value) || 0 })
                    }
                    placeholder="e.g., 2900 for $29"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Monthly Price (ZAR cents)</Label>
                  <Input
                    type="number"
                    value={createForm.price_monthly_zar}
                    onChange={(e) =>
                      setCreateForm({ ...createForm, price_monthly_zar: parseInt(e.target.value) || 0 })
                    }
                    placeholder="e.g., 52000 for R520"
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Annual Price/Month (USD cents)</Label>
                  <Input
                    type="number"
                    value={createForm.price_annual_usd}
                    onChange={(e) =>
                      setCreateForm({ ...createForm, price_annual_usd: parseInt(e.target.value) || 0 })
                    }
                    placeholder="e.g., 2400 for $24/mo billed annually"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Annual Price/Month (ZAR cents)</Label>
                  <Input
                    type="number"
                    value={createForm.price_annual_zar}
                    onChange={(e) =>
                      setCreateForm({ ...createForm, price_annual_zar: parseInt(e.target.value) || 0 })
                    }
                    placeholder="e.g., 43000 for R430/mo billed annually"
                  />
                </div>
              </div>
            </TabsContent>
            <TabsContent value="limits" className="space-y-4 py-4">
              <div className="space-y-2">
                <Label>Allowances (JSON)</Label>
                <Textarea
                  value={createAllowancesJson}
                  onChange={(e) => setCreateAllowancesJson(e.target.value)}
                  placeholder={'{\n  "rag_queries": 1000,\n  "pages_ingested": 500\n}'}
                  rows={5}
                  className="font-mono text-sm"
                />
              </div>
              <div className="space-y-2">
                <Label>Features</Label>
                <p className="text-sm text-muted-foreground">
                  Features can be added to this tier after creation via the Limits & Features dialog.
                </p>
              </div>
              <div className="space-y-2">
                <Label>Overage Pricing (JSON, USD cents per unit)</Label>
                <Textarea
                  value={createOveragePricingJson}
                  onChange={(e) => setCreateOveragePricingJson(e.target.value)}
                  placeholder={'{\n  "rag_queries": 3,\n  "pages_ingested": 8\n}'}
                  rows={5}
                  className="font-mono text-sm"
                />
              </div>
            </TabsContent>
          </Tabs>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setShowCreateDialog(false);
                resetCreateForm();
              }}
              disabled={isSaving}
            >
              Cancel
            </Button>
            <Button
              onClick={handleCreateTier}
              disabled={isSaving || !createForm.service_id || !createForm.name}
            >
              {isSaving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Create Tier
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Limits & Features Dialog — for JSON fields that don't fit inline */}
      <Dialog
        open={limitsDialogTier !== null}
        onOpenChange={(open) => !open && setLimitsDialogTier(null)}
      >
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Limits & Features</DialogTitle>
            <DialogDescription>
              {limitsDialogTier?.display_name} ({getServiceName(limitsDialogTier?.service_id ?? "")})
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Allowances (JSON)</Label>
              <Textarea
                value={allowancesJson}
                onChange={(e) => setAllowancesJson(e.target.value)}
                rows={5}
                className="font-mono text-sm"
                placeholder={'{\n  "storage_gib": 5,\n  "sftp_users": 5\n}'}
              />
            </div>
            <div className="space-y-2">
              <Label>Included Features</Label>
              {loadingFeatures ? (
                <div className="flex items-center gap-2 text-sm text-muted-foreground py-2">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Loading features...
                </div>
              ) : serviceFeatures.length === 0 ? (
                <p className="text-sm text-muted-foreground py-2">
                  No features defined for this service. Add features in the Products page first.
                </p>
              ) : (
                <div className="space-y-2 max-h-48 overflow-y-auto border rounded-md p-3">
                  {serviceFeatures.map((feat) => (
                    <div key={feat.id} className="flex items-center gap-2">
                      <Checkbox
                        id={`feat-${feat.id}`}
                        checked={tierFeatureIds.has(feat.id)}
                        onCheckedChange={(checked) =>
                          toggleTierFeature(feat.id, checked === true)
                        }
                      />
                      <label
                        htmlFor={`feat-${feat.id}`}
                        className="text-sm cursor-pointer flex-1"
                      >
                        {feat.display_name}
                        <span className="text-xs text-muted-foreground ml-2">
                          {feat.sku_code}
                        </span>
                      </label>
                    </div>
                  ))}
                </div>
              )}
            </div>
            <div className="space-y-2">
              <Label>Overage Pricing (JSON, USD cents per unit)</Label>
              <Textarea
                value={overagePricingJson}
                onChange={(e) => setOveragePricingJson(e.target.value)}
                rows={5}
                className="font-mono text-sm"
                placeholder={'{\n  "per_gib_usd": 10\n}'}
              />
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setLimitsDialogTier(null)}
              disabled={isSaving}
            >
              Cancel
            </Button>
            <Button onClick={handleSaveLimits} disabled={isSaving}>
              {isSaving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Save Limits
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog
        open={deleteTier !== null}
        onOpenChange={(open) => !open && setDeleteTier(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Deactivate Tier?</AlertDialogTitle>
            <AlertDialogDescription>
              This will deactivate {deleteTier?.display_name}. Existing
              subscriptions will continue to work, but new subscriptions will not
              be available on this tier.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => deleteTier && handleDeleteTier(deleteTier)}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Deactivate
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
